import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { ArrowRight, ShieldCheck, Printer, Beaker } from "lucide-react";

export default function Landing() {
  return (
    <div className="min-h-screen bg-zinc-950 text-zinc-100 relative overflow-hidden noise-overlay">
      {/* Top bar */}
      <header className="border-b border-zinc-800 px-8 py-4 flex items-center justify-between relative z-10">
        <div className="font-display text-xl font-bold tracking-tight">
          mixo<span className="text-red-500"> </span>vap
        </div>
        <div className="flex gap-2 items-center">
          <Link to="/login" data-testid="landing-login-link" className="text-sm text-zinc-400 hover:text-white px-4 py-2 transition-colors">
            Connexion
          </Link>
          <Link to="/register" data-testid="landing-register-link">
            <Button className="bg-red-500 hover:bg-red-600 text-white rounded-none font-medium" data-testid="landing-cta-hero">
              Commencer <ArrowRight className="ml-2 w-4 h-4" />
            </Button>
          </Link>
        </div>
      </header>

      {/* Bento hero grid */}
      <section className="px-8 pt-16 pb-24 max-w-7xl mx-auto relative z-10">
        <div className="grid grid-cols-12 gap-0 border border-zinc-800 fade-stagger">
          {/* Hero copy */}
          <div className="col-span-12 lg:col-span-8 p-12 border-b lg:border-b-0 lg:border-r border-zinc-800">
            <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-red-500 mb-6">
              CONFORMITÉ · TPD · UFI · DLUO
            </div>
            <h1 className="font-display text-5xl sm:text-6xl lg:text-7xl font-extrabold tracking-tighter leading-[0.95]">
              Étiquettes conformes<br />
              pour e-liquides<br />
              <span className="text-red-500">en vrac.</span>
            </h1>
            <p className="mt-8 text-zinc-400 text-lg max-w-xl leading-relaxed">
              Le logiciel d'étiquetage pensé pour les boutiques de cigarette électronique.
              Générez, imprimez, et exportez en PDF vos étiquettes 30/50/100/200/500 ml
              avec toutes les mentions légales obligatoires.
            </p>
            <div className="mt-10 flex gap-3">
              <Link to="/register">
                <Button className="bg-red-500 hover:bg-red-600 text-white rounded-none px-6 py-6 text-base font-medium" data-testid="landing-cta-primary">
                  Essayer maintenant <ArrowRight className="ml-2 w-4 h-4" />
                </Button>
              </Link>
              <Link to="/login">
                <Button variant="outline" className="rounded-none border-zinc-700 text-zinc-100 bg-transparent hover:bg-zinc-900 hover:text-white px-6 py-6 text-base" data-testid="landing-cta-login">
                  J'ai déjà un compte
                </Button>
              </Link>
            </div>
          </div>

          {/* Pricing card — pay-per-use credits */}
          <div className="col-span-12 lg:col-span-4 p-10 bg-zinc-950">
            <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-zinc-500 mb-4">Tarif · payez à l'usage</div>
            <div className="flex items-baseline gap-1">
              <div className="font-display text-6xl font-extrabold tracking-tighter">0,10€</div>
              <div className="text-zinc-500 text-sm ml-1">/ étiq.</div>
            </div>
            <div className="text-xs text-zinc-500 mt-2 font-mono">dès le pack 2000 · TTC · sans abonnement</div>
            <div className="mt-4 border-l-2 border-red-500 pl-3 text-xs text-zinc-300">
              <span className="text-green-400 font-semibold">5 crédits offerts</span> à l'inscription
            </div>
            <div className="mt-6 space-y-3 text-sm text-zinc-300">
              <div className="flex items-start gap-2"><ShieldCheck className="w-4 h-4 text-red-500 mt-0.5 shrink-0" /> Mentions légales conformes</div>
              <div className="flex items-start gap-2"><Beaker className="w-4 h-4 text-red-500 mt-0.5 shrink-0" /> Bibliothèque de liquides illimitée</div>
              <div className="flex items-start gap-2"><Printer className="w-4 h-4 text-red-500 mt-0.5 shrink-0" /> Export PDF & impression navigateur</div>
            </div>
            <Link to="/register" className="block mt-8">
              <Button className="w-full bg-white text-black hover:bg-zinc-200 rounded-none font-semibold" data-testid="landing-pricing-cta">
                Créer mon compte
              </Button>
            </Link>
          </div>
        </div>

        {/* Credit packs strip */}
        <div className="grid grid-cols-2 md:grid-cols-5 border-l border-r border-b border-zinc-800">
          {[
            { credits: 100, price: 30, unit: "0,30" },
            { credits: 200, price: 50, unit: "0,25" },
            { credits: 500, price: 100, unit: "0,20" },
            { credits: 1000, price: 150, unit: "0,15" },
            { credits: 2000, price: 200, unit: "0,10", best: true },
          ].map((p) => (
            <div key={p.credits} className={`p-6 border-t border-zinc-800 md:border-t-0 md:[&:not(:first-child)]:border-l relative ${p.best ? "bg-red-500/5" : ""}`}>
              {p.best && <div className="absolute top-0 right-0 bg-red-500 text-white text-[9px] font-mono uppercase tracking-widest px-2 py-1">Best</div>}
              <div className="font-mono text-[10px] text-zinc-500 uppercase tracking-widest">Pack</div>
              <div className="font-display text-3xl font-extrabold tracking-tighter mt-1">{p.credits.toLocaleString("fr-FR")}</div>
              <div className="text-xs text-zinc-500 mt-0.5">étiquettes</div>
              <div className="mt-4 font-display text-xl font-bold">{p.price}€ <span className="text-xs text-zinc-500 font-mono">TTC</span></div>
              <div className="text-[10px] font-mono text-zinc-500 mt-1">{p.unit}€ / étiq.</div>
            </div>
          ))}
        </div>

        {/* Feature strips */}
        <div className="grid grid-cols-12 border-l border-r border-b border-zinc-800">
          <FeatureCell title="Formats libres" mono="30 · 50 · 100 · 200 · 500 ml" desc="Choisissez la taille du flacon, adaptez la dimension de l'étiquette." />
          <FeatureCell title="Champs réglementaires" mono="UFI · DLUO · LOT · +18 · ⌀" desc="Toutes les mentions légales imprimées automatiquement." />
          <FeatureCell title="Votre identité" mono="LOGO · COORDONNÉES · SIRET" desc="Votre logo et coordonnées apparaissent sur chaque étiquette." />
        </div>
      </section>

      <footer className="border-t border-zinc-800 px-8 py-6 text-xs text-zinc-500 font-mono relative z-10">
        © {new Date().getFullYear()} mixo vap — Étiquettes conformes à la réglementation française
      </footer>
    </div>
  );
}

function FeatureCell({ title, mono, desc }) {
  return (
    <div className="col-span-12 md:col-span-4 p-8 border-t border-zinc-800 md:border-t-0 md:[&:not(:first-child)]:border-l">
      <div className="font-mono text-[10px] tracking-[0.2em] text-red-500 uppercase">{mono}</div>
      <div className="font-display text-2xl font-bold tracking-tight mt-4">{title}</div>
      <p className="text-sm text-zinc-400 mt-3 leading-relaxed">{desc}</p>
    </div>
  );
}
